(function (root, factory) {
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  else root.IllegalShippingQuotes = api;
})(typeof window === "undefined" ? globalThis : window, function () {
  "use strict";
  const error = (code) => Object.assign(new Error(code), { code });
  const canonical = (v) =>
    Array.isArray(v)
      ? v.map(canonical)
      : v && typeof v === "object"
        ? Object.fromEntries(
            Object.keys(v)
              .sort()
              .map((k) => [k, canonical(v[k])]),
          )
        : v;
  const stable = (v) => JSON.stringify(canonical(v));
  // Shopify can regenerate discount keys/timestamps on every cart read.
  // Keep eligibility and amounts in identity, not calculation-instance metadata.
  const discounts = (v) =>
    Array.isArray(v)
      ? v.map(discounts)
      : v && typeof v === "object"
        ? Object.fromEntries(
            Object.entries(v)
              .filter(([k]) => k !== "key" && k !== "created_at")
              .map(([k, value]) => [k, discounts(value)]),
          )
        : v;
  function cartContext(cart, market) {
    if (
      !cart ||
      !Array.isArray(cart.items) ||
      !/^[A-Z]{3}$/.test(cart.currency || "") ||
      !Number.isSafeInteger(cart.total_price) ||
      cart.total_price < 0
    )
      throw error("malformed");
    return stable({
      scope: "cart",
      market,
      currency: cart.currency,
      total: cart.total_price,
      original: cart.original_total_price,
      discounts: discounts(cart.cart_level_discount_applications || []),
      codes: cart.discount_codes || [],
      attributes: cart.attributes || {},
      items: cart.items
        .map((i) => ({
          variant: String(i.variant_id || i.id),
          quantity: i.quantity,
          properties: i.properties || {},
          plan: i.selling_plan_allocation || null,
          parent: i.parent_relationship || null,
          components: i.item_components || null,
          shipping: i.requires_shipping,
          price: i.final_line_price,
          discounts: discounts(
            i.line_level_discount_allocations || i.discounts || [],
          ),
        }))
        .sort((a, b) => stable(a).localeCompare(stable(b))),
    });
  }
  function amount(value) {
    if (typeof value !== "string" || !/^\d+(\.\d{1,2})?$/.test(value))
      throw error("malformed");
    const [whole, fraction = ""] = value.split(".");
    const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
    if (!Number.isSafeInteger(cents)) throw error("malformed");
    return cents;
  }
  function rates(value, currency) {
    if (!value || !Array.isArray(value.shipping_rates))
      throw error("malformed");
    return value.shipping_rates.map((r) => {
      if (
        typeof r.name !== "string" ||
        !r.name.trim() ||
        r.currency !== currency ||
        (r.presentment_name != null && typeof r.presentment_name !== "string")
      )
        throw error("currency");
      return {
        name: r.presentment_name || r.name,
        amount: amount(r.price),
        currency,
      };
    });
  }
  class Controller {
    constructor({
      cart,
      fetch,
      root = "/",
      market = "",
      now = Date.now,
      sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      lock = (task) => task(),
      onChange = () => {},
      onUncertain = () => {},
      timeout = 20000,
      requestTimeout = 8000,
      ttl = 120000,
    }) {
      Object.assign(this, {
        cart,
        fetch,
        root,
        market,
        now,
        sleep,
        lock,
        onChange,
        onUncertain,
        timeout,
        requestTimeout,
        ttl,
      });
      this.generation = 0;
      this.tail = Promise.resolve();
      this.destination = null;
      // Session-local counters only. No destination, cart token or line data.
      this.diagnostics = {
        attempts: 0,
        available: 0,
        invalidations: 0,
        discarded: 0,
        lastLatencyMs: null,
        totalLatencyMs: 0,
        errors: {},
      };
      this.value = { status: "destination-needed", rates: [] };
      this.context = cartContext(cart.cart, market);
      this.unsubscribe = cart.subscribe((state) => {
        const next = cartContext(state.cart, this.market);
        if (
          state.pending ||
          state.locked ||
          state.error ||
          next !== this.context
        )
          this.invalidate();
        this.context = next;
      });
    }
    emit(status, extra = {}) {
      if (status === "available") this.diagnostics.available++;
      if (status === "invalidated") this.diagnostics.invalidations++;
      if (status === "unavailable" || status === "invalid-destination") {
        const category = extra.reason || status;
        this.diagnostics.errors[category] =
          (this.diagnostics.errors[category] || 0) + 1;
      }
      clearTimeout(this.expiryTimer);
      this.value = { status, rates: [], ...extra };
      if (status === "available") {
        this.expiryTimer = setTimeout(
          () => this.expire(),
          Math.max(0, extra.expiresAt - this.now()),
        );
        this.expiryTimer.unref?.();
      }
      try {
        this.onChange(this.value);
      } catch (_) {}
      return this.value;
    }
    invalidate() {
      this.generation++;
      return this.emit(this.destination ? "invalidated" : "destination-needed");
    }
    setDestination(input) {
      const d = {
        country: String(input.country || "").trim(),
        province: String(input.province || "").trim(),
        zip: String(input.zip || "").trim(),
      };
      if (stable(d) !== stable(this.destination)) {
        this.destination = d;
        this.invalidate();
      }
    }
    expire() {
      if (
        this.value.status === "available" &&
        this.now() >= this.value.expiresAt
      )
        this.invalidate();
    }
    async request(path, options = {}) {
      let timer;
      const operation = Promise.resolve()
        .then(() =>
          this.fetch(this.root + path, {
            credentials: "same-origin",
            cache: "no-store",
            ...options,
          }),
        )
        .then(async (response) => {
          if (!response.ok)
            throw error(
              response.status === 422 || response.status === 400
                ? "invalid-destination"
                : response.status === 429
                  ? "rate-limited"
                  : "unavailable",
            );
          try {
            return await response.json();
          } catch (_) {
            throw error("malformed");
          }
        });
      try {
        return await Promise.race([
          operation,
          new Promise((_, reject) => {
            timer = setTimeout(() => {
              this.quarantine();
              reject(error("timeout"));
            }, this.requestTimeout);
          }),
        ]);
      } finally {
        clearTimeout(timer);
      }
    }
    quarantine() {
      this.uncertain = true;
      try {
        this.onUncertain();
      } catch (_) {}
    }
    async idle() {
      const started = this.now();
      while (this.cart.state().pending) {
        if (this.now() - started >= this.timeout || this.cart.state().locked)
          throw error("invalidated");
        await this.sleep(50);
      }
      if (this.cart.state().error || this.cart.state().locked)
        throw error("invalidated");
    }
    calculate() {
      const d = this.destination;
      if (
        !d?.country ||
        d.country.length > 100 ||
        d.zip.length > 32 ||
        d.province.length > 100 ||
        /[\x00-\x1f]/.test(stable(d))
      )
        return Promise.resolve(this.emit("invalid-destination"));
      this.expire();
      const key = stable({
        d,
        context: this.context,
        generation: this.generation,
      });
      if (this.active?.key === key) return this.active.promise;
      if (this.value.status === "available" && this.value.key === key)
        return Promise.resolve(this.value);
      const generation = this.generation;
      const startedAt = this.now();
      this.diagnostics.attempts++;
      this.emit("calculating");
      const current = () => this.generation === generation;
      const task = async () => {
        if (!current()) return this.value;
        if (this.uncertain) throw error("unavailable");
        await this.idle();
        if (!current()) return this.value;
        const before = await this.request("cart.js");
        const signature = cartContext(before, this.market);
        if (
          !current() ||
          this.cart.state().pending ||
          signature !== cartContext(this.cart.cart, this.market)
        )
          throw error("invalidated");
        if (
          !before.items.length ||
          before.requires_shipping === false ||
          before.items.every((i) => i.requires_shipping === false)
        )
          return this.emit("no-quote", { reason: "no-shipping-items" });
        const params = new URLSearchParams();
        Object.entries(d).forEach(([k, v]) =>
          params.set(`shipping_address[${k}]`, v),
        );
        const query = params.toString();
        try {
          await this.request("cart/prepare_shipping_rates.json?" + query, {
            method: "POST",
            headers: { Accept: "application/json" },
          });
        } catch (e) {
          // A lost connection or 5xx cannot prove that preparation was rejected.
          if (!["invalid-destination", "rate-limited"].includes(e.code))
            this.quarantine();
          throw e;
        }
        const start = this.now();
        let result;
        let attempt = 0;
        do {
          await this.sleep(Math.min(500 * 2 ** attempt++, 2000));
          try {
            result = await this.request(
              "cart/async_shipping_rates.json?" + query,
            );
          } catch (e) {
            this.quarantine();
            throw e;
          }
          if (result !== null) break;
        } while (this.now() - start < this.timeout);
        if (result === null) {
          this.quarantine();
          throw error("timeout");
        }
        const methods = rates(result, before.currency);
        const after = await this.request("cart.js");
        if (
          !current() ||
          this.cart.state().pending ||
          this.cart.state().locked ||
          signature !== cartContext(after, this.market) ||
          signature !== cartContext(this.cart.cart, this.market)
        )
          throw error("invalidated");
        return this.emit(methods.length ? "available" : "no-quote", {
          key,
          scope: "cart",
          context: signature,
          rates: methods,
          currency: before.currency,
          subtotal: before.total_price,
          quotedAt: this.now(),
          expiresAt: this.now() + this.ttl,
        });
      };
      const promise = this.tail
        .catch(() => {})
        .then(() => this.lock(task))
        .catch((e) => {
          if (current())
            return this.emit(
              e.code === "invalid-destination"
                ? e.code
                : e.code === "invalidated"
                  ? "invalidated"
                  : "unavailable",
              { reason: e.code || "network" },
            );
          return this.value;
        })
        .finally(() => {
          if (!current()) this.diagnostics.discarded++;
          this.diagnostics.lastLatencyMs = Math.max(0, this.now() - startedAt);
          this.diagnostics.totalLatencyMs += this.diagnostics.lastLatencyMs;
          if (this.active?.promise === promise) this.active = null;
        });
      this.active = { key, promise };
      this.tail = promise;
      return promise;
    }
    destroy() {
      this.unsubscribe();
      this.invalidate();
      clearTimeout(this.expiryTimer);
    }
  }
  return { Controller, cartContext, amount, rates };
});
